<template>
  <avtalat-modal
    :closeOnClickOutside="true"
    :closeOnEsc="true"
    :closeFn="onCloseClosedArendeModal"
    :idealHeight="1100"
  >
    <div class="flex h-full flex-col gap-sm px-md pt-md">
      <div class="flex flex-col gap-sm">
        <div class="flex">
          <button
            class="absolute right-md top-sm p-xs"
            data-testid="closed-arende-close-button"
            @click="onCloseClosedArendeModal"
          >
            <avtalat-icon class="text-black" id="kryss-enkelt" :size="15" />
          </button>
        </div>
        <div class="type-heading-md" data-testid="closed-arende-subject">
          {{ arendeStore.arende.data?.rubrik }}
        </div>
        <ArendeIdDate
          :id="arendeStore.arende.data?.id"
          :date="formatDateToYYYYMMDD(arendeStore.arende.data?.createdAt)"
        />
        <FormGroup>
          <div>
            <div class="type-body-md">Kontaktorsak</div>
            <span class="font-bold" data-testid="closed-arende-kontaktorsak">{{
              arendeStore.arende.data?.rubrik
            }}</span>
          </div>
          <div v-if="arendeStore.arende.data?.fortydligadKontaktorsak">
            <div class="type-body-md">Förtydligad kontaktorsak</div>
            <span
              class="font-bold"
              data-testid="closed-arende-fortydligad-kontaktorsak"
              >{{ arendeStore.arende.data?.fortydligadKontaktorsak }}</span
            >
          </div>
          <div data-testid="closed-arende-samtalsamnen">
            <div class="type-body-md mb-2xs">
              Samtalsämne ({{
                arendeStore.arende.data?.samtalsamneIds !== null
                  ? arendeStore.arende.data?.samtalsamneIds?.length
                  : 0
              }})
            </div>
            <ChipsContainer
              v-if="arendeStore.arende.data?.samtalsamneIds !== null"
            >
              <ChipsItem
                v-for="(samtalsamne, idx) in arendeStore.arende.data
                  ?.samtalsamneIds"
                :removable="false"
                :key="idx"
              >
                {{ chipLabel(samtalsamne) }}
              </ChipsItem>
            </ChipsContainer>
          </div>
        </FormGroup>
        <FormGroup>
          <div class="type-body-md mb-2xs">
            Interna Kommentarer ({{ arendeInternaKommentarer?.length }})
          </div>
          <div class="custom-scrollbar max-h-[300px] overflow-y-auto">
            <InternaKommentarerList
              heading=""
              :interna-kommentarer="arendeInternaKommentarer ?? []"
              :messageAlignement="false"
              :showVerified="true"
              :disable-expand="true"
              :can-edit="false"
            />
          </div>
        </FormGroup>
        <FormGroup>
          <div class="type-body-md mb-2xs">
            Säkra meddelanden ({{ arendeSakraMeddelanden?.length }})
          </div>
          <div class="custom-scrollbar max-h-[300px] overflow-y-auto">
            <SakraMeddelandenList
              heading=""
              :sakra-meddelanden="arendeSakraMeddelanden ?? []"
              :show-verified="false"
              :disable-expand="true"
            />
          </div>
        </FormGroup>
        <FormGroup class="type-body-md">
          <ClosedArendeInfo />
        </FormGroup>
      </div>
      <div class="mt-auto flex justify-end space-x-sm pb-md">
        <avtalat-button
          outlined
          @click="onCloseClosedArendeModal"
          :loading="arendeStore.arende.isSaving"
          data-testid="closed-arende-ingen-atgard-button"
          >Ingen åtgärd</avtalat-button
        >
        <avtalat-button
          @click="onReopenArende"
          :loading="arendeStore.arende.isSaving"
          data-testid="closed-arende-ingen-ateroppna-button"
          >Återöppna ärende</avtalat-button
        >
      </div>
    </div>
  </avtalat-modal>
</template>

<script setup lang="ts">
import { inject } from "vue";

import { formatDateToYYYYMMDD } from "@/common/formatters";
import ArendeIdDate from "@/components/arende-components/ArendeIdDate.vue";
import InternaKommentarerList from "@/components/arende-components/InternaKommentarerList.vue";
import SakraMeddelandenList from "@/components/arende-components/SakraMeddelandenList.vue";
import ChipsContainer from "@/components/ui/ChipsContainer.vue";
import ChipsItem from "@/components/ui/ChipsItem.vue";
import FormGroup from "@/components/ui/FormGroup.vue";
import { useApi } from "@/composables/useApi";
import { useNavigation } from "@/composables/useNavigation";
import { useConfigStore, useIndividStore } from "@/stores";

import { useAnstalldarendeStore } from "../../store";
import ClosedArendeInfo from "./ClosedArendeInfo.vue";

const configStore = useConfigStore();
const individStore = useIndividStore();
const arendeStore = useAnstalldarendeStore();
const navigation = useNavigation();
const apiClient = useApi();

const openArendenAndReload = inject("openArendenAndReload", () => {});

const onCloseClosedArendeModal = () => {
  navigation.closeArendeView();
};

const onReopenArende = async () => {
  await arendeStore.reopenArende(
    {
      arendeId: arendeStore.arende.data?.id as number,
      individId: individStore.individ.data?.id as string,
      isVerified: individStore.verification.isVerified,
      isManuallyVerified: individStore.verification.isManuallyVerified,
    },
    apiClient,
  );

  openArendenAndReload();
};

const chipLabel = (id: string) => configStore.getKontaktorsakById(id)?.namn;
const arendeSakraMeddelanden = arendeStore.arende.data?.sakraMeddelanden;
const arendeInternaKommentarer = arendeStore.arende.data?.internaKommentarer;

export type SamtalsamneLabel = {
  id: string;
  label: string;
};
</script>
